<template>
  <b-card class="okr-overview-card position-relative">
    <!-- Botão de expansão fixo na borda direita -->
    <b-button
      variant="outline-primary"
      class="expand-button position-absolute"
      @click="showDetails = !showDetails"
      :title="showDetails ? 'Recolher detalhes' : 'Expandir detalhes'"
    >
      <feather-icon 
        :icon="showDetails ? 'ChevronUpIcon' : 'ChevronDownIcon'" 
        size="18" 
      />
    </b-button>

    <b-row>
      <b-col cols="12" md="3" class="mb-2 mb-md-0">
        <b-media no-body>
          <b-media-aside class="mr-2">
            <b-avatar
              size="40"
              variant="light-primary"
              class="badge-minimal"
              style="cursor: pointer"
            >
              <img
                v-if="consultantImg"
                :src="consultantImg"
                height="40"
              />
              <img
                v-else
                src="~@/assets/images/logo/futuro.svg"
                height="40"
              />
            </b-avatar>
          </b-media-aside>
          <b-media-body class="my-auto">
            <h5 class="font-weight-bolder mb-0">
              {{ consultantName }}
            </h5>
            <b-card-text class="font-small-3 mb-0">
              {{ consultantContractType }} 
              {{ consultantRole != ''? ` - ${consultantRole}`:'' }}
            </b-card-text>
          </b-media-body>
        </b-media>
      </b-col>
      <b-col cols="12" md="9" class="mb-2 mb-md-0">
        <div class="d-flex w-100 stats-container">
          <div class="stats-item">
            <h5 class="font-weight-bolder mb-0">
              {{ leadsQuantity }}
            </h5>
            <b-card-text class="font-small-3 mb-0">
              Leads
            </b-card-text>
          </div>
          <div class="stats-item">
            <h5 class="font-weight-bolder mb-0">
              {{ hotLeadsQuantity }}
            </h5>
            <b-card-text class="font-small-3 mb-0">
              Leads quentes
            </b-card-text>
          </div>
          <div class="stats-item">
            <h5 class="font-weight-bolder mb-0">
              {{ eaQuantity }}
            </h5>
            <b-card-text class="font-small-3 mb-0">
              EA
            </b-card-text>
          </div>
          <div class="stats-item">
            <h5 class="font-weight-bolder mb-0">
              {{ efQuantity }}
            </h5>
            <b-card-text class="font-small-3 mb-0">
              EF
            </b-card-text>
          </div>
          <div class="stats-item">
            <h5 class="font-weight-bolder mb-0">
              {{ epQuantity }}
            </h5>
            <b-card-text class="font-small-3 mb-0">
              EP
            </b-card-text>
          </div>
          <div class="stats-item">
            <h5 class="font-weight-bolder mb-0">
              {{ okrTotal | toCurrency }}
            </h5>
            <b-card-text class="font-small-3 mb-0">
              OKR Total
            </b-card-text>
          </div>
          <div class="stats-item">
            <h5 class="font-weight-bolder mb-0">
              {{ progress }}%
            </h5>
            <b-card-text class="font-small-3 mb-0">
               Progresso
            </b-card-text>
          </div>
        </div>
      </b-col>
    </b-row>
    <div v-if="loading" class="text-center">
      <b-spinner class="mt-2" variant="secondary" large />
    </div>
    <transition name="expand">
      <div v-if="showDetails && !loading" class="expand-body">
        <b-row>
          <div class="block-divider my-2"></div>
        </b-row>
        <b-row>
          <div class="mx-2 w-100">
            <h5>OKR Pró-Labore</h5>
            <div class="d-flex justify-content-between align-items-center mb-1">
              <h6 class="mb-0">
                {{ prolaboreAmount | toCurrency }} /
                {{ prolaboreMeta | toCurrency }}
              </h6>
              <h6 class="text-white-50">{{ prolaboreProgress }}%</h6>
            </div>
            <b-progress
              :value="prolaboreProgress"
              :max="150"
              variant="success"
              height="1.5rem"
              class="progress-bar-custom"
            />
          </div>
        </b-row>
        <div v-if="loadingTasks" class="text-center">
          <b-spinner class="mt-2" variant="secondary" large />
        </div>
        <b-row v-else>
          <TaskTrackerTable
            class="mx-2 mb-2 w-100"
            title=""
            :tasks="taskList"
          />
        </b-row>
      </div>
    </transition>
  </b-card>
</template>
<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BForm,
  BButton,
  BCard,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import useVuelidate from '@vuelidate/core'
import { required, email, helpers, requiredIf } from '@vuelidate/validators'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import { getVueSelectErrorClass } from '@/helpers/validators'
import * as types from '../store/types'
import { VMoney } from "v-money";
import { getBRLFormat } from "@/helpers/formatting";
import VueSlider from "vue-slider-component";
import { floatToMoney, moneyToFloat } from "@/helpers/converters";
import * as structcureTypes from "@/constants/structure_options";
import TaskTrackerTable from './TaskTrackerTable.vue'
import { processTaskList } from "@/helpers/okr";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BForm,
    BButton,
    BSpinner,
    vSelect,
    VueSlider,
    TaskTrackerTable
  },
  directives: {
    Ripple,
    money: VMoney,
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() }
  },
  props: {
    year: {
      type: Number,
      required: true
    },
    quarter: {
      type: Number,
      required: true
    },
    search: {
      type: Function,
      required: true
    },
    consultantId: {
      type: Number,
      required: true
    },
    consultantName: {
      type: String,
      required: true
    },
    consultantRole: {
      type: String,
      required: true
    },
    consultantContractType: {
      type: String,
      required: false,
      default: ''
    },
    consultantImg: {
      type: [String, null],
      required: false,
      default: null
    },
    okrTotal: {
      type: Number,
      required: true
    },
    prolaboreAmount: {
      type: Number,
      required: true
    },
    prolaboreMeta: {
      type: Number,
      required: true
    },
    leadsQuantity: {
      type: Number,
      required: true
    },
    hotLeadsQuantity: {
      type: Number,
      required: true
    },
    eaQuantity: {
      type: Number,
      required: true,
      default: 512
    },
    efQuantity: {
      type: Number,
      required: true
    },
    epQuantity: {
      type: Number,
      required: true
    },
    progress: {
      type: Number,
      required: false,
    }, 
  },
  data() {
    return {
      loading: false,
      loadingTasks: false,
      showDetails: false,
      currentPerformanceData: undefined,
      dir: "ltr"
    }
  },
  watch: {
    showDetails(){
      if(this.showDetails){
        this.loadData();
      }
    },
  },
  computed: {
    moneyFormat: function () {
      return getBRLFormat();
    },
    prolaboreProgress() {
      return this.prolaboreMeta > 0
        ? Math.round((this.prolaboreAmount / this.prolaboreMeta) * 100) : 0;
    },
    taskList() {
      return processTaskList(this.currentPerformanceData);
    }
  },
  methods: {
    ...mapActions({
      getConsultantEpPercentage: types.GET_CONSULTANT_EP_PERCENTAGE,
      getCurrentPerfomanceConsultant: types.GET_CURRENT_PERFORMANCE_CONSULTANT
    }),
    loadData(){
      this.loadingTasks = true
      this.getCurrentPerfomanceConsultant({
        year: this.year,
        quarter: this.quarter,
        structure_type: structcureTypes.STRUCTURE,
        consultant_id: this.consultantId
      })
        .then(response => {
          this.currentPerformanceData = response.data
        })
        .catch(error => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: error?.response?.data?.message || 
                'Ocorreu um erro ao carregar o OKR. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.loadingTasks = false
        })
    },
    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError)
    }
  },
}
</script>
<style lang="scss">
$themeColor: #f26237;
@import "~vue-slider-component/lib/theme/default.scss";

.dark-layout input.form-control:disabled{
  background-color: #242b3d;
}

// Container do card OKR com posicionamento relativo
.okr-overview-card {
  position: relative;
  padding-right: 50px; // Espaço para o botão de expansão
  
  @media (max-width: 767.98px) {
    padding-right: 20px;
  }
}

// Botão de expansão fixo
.expand-button {
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 767.98px) {
    right: 10px;
    width: 32px;
    height: 32px;
    
    &:hover {
      transform: translateY(-50%) scale(1.05);
    }
  }
}

// Container das estatísticas com melhor responsividade
.stats-container {
  flex-wrap: wrap;
  gap: 8px;
  
  @media (max-width: 767.98px) {
    justify-content: space-between;
  }
  
  @media (max-width: 575.98px) {
    flex-direction: column;
    gap: 12px;
  }
}

// Item individual das estatísticas
.stats-item {
  flex: 1;
  min-width: 80px;

  &:not(:first-child) {
    @media (min-width: 768px) {
      border-left: 1px solid #ebe9f1;
      padding-left: 12px;
    }
  }

  @media (max-width: 767.98px) {
    min-width: 70px;
    text-align: center;
  }

  @media (max-width: 575.98px) {
    min-width: 100%;
    text-align: left;
    padding: 8px 0;
    border-bottom: 1px solid #eee;

    &:last-child {
      border-bottom: none;
    }
  }

  h5 {
    @media (max-width: 575.98px) {
      font-size: 1.1rem;
    }
  }
}

// Smooth expand/collapse transition
.expand-enter-active,
.expand-leave-active {
  transition: max-height 0.35s ease, opacity 0.25s ease;
  max-height: 800px;
  overflow: hidden;
}
.expand-enter,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

// Container do progresso
.progress-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  
  @media (max-width: 767.98px) {
    justify-content: center;
    margin-top: 15px;
  }
  
  @media (max-width: 575.98px) {
    justify-content: flex-start;
    margin-top: 10px;
  }
}

// Manter classe original para compatibilidade (caso seja usada em outros lugares)
.okr_total_title {
  display: flex;
  justify-content: end;
  align-items: end;
}

.block-divider {
  width: 100%;
  height: 1px;
  background-color: #dcdcdc;
}

.dark-layout .block-divider {
  background-color: #1b2132;
}

// Estilos específicos para tema escuro
.dark-layout {
  .stats-item {
    @media (max-width: 575.98px) {
      border-bottom-color: #3a4050;
    }

    &:not(:first-child) {
      @media (min-width: 768px) {
        border-left-color: #3b4253;
      }
    }
  }
  
  .expand-button {
    background-color: #3a4050;
    border-color: #5a6c7d;
    color: #d0d2d6;
    
    &:hover {
      background-color: #5a6c7d;
      border-color: #6c7b8a;
    }
  }

  .progress-bar-custom {
    border-radius: 8px;
    overflow: hidden;
  }

}
</style>
